import { useObserva } from "@morganisbatman/observa-client-react";
import { useMemo, useState, type MutableRefObject } from "react";

import { trpc } from "../trpc";
import {
  type Question,
  type TutorMessage,
  TUTOR_TURN_CAP,
} from "./types";

interface UseTutorChatArgs {
  /** Currently-displayed question. Bubbles + sends bind to this. */
  current: Question | null;
  /** Caller's session id ref — surfaced to observa for funnel join. */
  sessionIdRef: MutableRefObject<string>;
}

interface UseTutorChatReturn {
  messages: TutorMessage[];
  /** Server-issued session id; null until the first send completes. */
  serverSessionId: string | null;
  input: string;
  setInput: (v: string) => void;
  sending: boolean;
  error: string | null;
  /** Count of user turns so far — drives the input → "I get it" swap. */
  userTurnCount: number;
  /** True when the cap is reached and the user should see the CTA. */
  capped: boolean;
  /**
   * Send a draft. Pass `textOverride` to fire the chip flow; otherwise
   * reads from input state. No-op when no current question, an empty
   * draft, or the turn cap has been reached.
   */
  send: (textOverride?: string) => Promise<void>;
  /** Seed the kickoff bubble for a freshly-wrong answer. */
  seedKickoff: (chosenLabel: string) => void;
  /** Wipe state on advance to the next question. */
  reset: () => void;
}

// Socratic tutor state machine. Owns the message list, the optimistic
// user-turn rendering, server round-trip, error UX (incl. friendlier
// rate-limit copy), and the 4-turn cap.
export function useTutorChat({
  current,
  sessionIdRef,
}: UseTutorChatArgs): UseTutorChatReturn {
  const observa = useObserva();
  const [messages, setMessages] = useState<TutorMessage[]>([]);
  const [serverSessionId, setServerSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userTurnCount = useMemo(
    () => messages.filter((m) => m.role === "user").length,
    [messages],
  );
  const capped = userTurnCount >= TUTOR_TURN_CAP;

  function reset(): void {
    setMessages([]);
    setServerSessionId(null);
    setInput("");
    setError(null);
  }

  function seedKickoff(chosenLabel: string): void {
    const chosenText = current?.choices.find(
      (c) => c.label === chosenLabel,
    )?.text;
    const pick = chosenText
      ? `${chosenLabel} — "${chosenText}"`
      : chosenLabel;
    setMessages([
      {
        role: "assistant",
        content: `What made you pick ${pick}? Walk me through your thinking.`,
      },
    ]);
    setServerSessionId(null);
    setInput("");
    setError(null);
  }

  async function send(textOverride?: string): Promise<void> {
    if (!current) return;
    // Re-entrancy guard: a second send (rapid Enter / chip double-tap) while
    // one is in flight would push a duplicate optimistic turn and race the
    // cap check off a stale `userTurnCount` closure.
    if (sending) return;
    const draft = (textOverride ?? input).trim();
    if (!draft) return;
    if (userTurnCount >= TUTOR_TURN_CAP) return;

    setSending(true);
    setError(null);
    // Optimistically render the user's turn — feels much better than
    // waiting for the round trip before showing what they typed. The cap is
    // re-checked off the live `prev` so a stale closure can't blow past it,
    // and the analytics turnIndex is derived from the same live count.
    let turnIndex = userTurnCount;
    setMessages((prev) => {
      const liveTurns = prev.filter((m) => m.role === "user").length;
      if (liveTurns >= TUTOR_TURN_CAP) return prev;
      turnIndex = liveTurns;
      return [...prev, { role: "user", content: draft }];
    });
    setInput("");
    void observa.event({
      eventType: "practice.tutor_message_sent",
      payload: {
        sessionId: sessionIdRef.current,
        questionId: current.id,
        // 0-indexed: the message we just optimistically added.
        turnIndex,
      },
    });

    try {
      const res = await trpc.tutor.explain.mutate({
        questionId: current.id,
        userMessage: draft,
        ...(serverSessionId ? { tutorSessionId: serverSessionId } : {}),
        taskName: "Socratic walk-through",
      });
      setServerSessionId(res.tutorSessionId);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.reply },
      ]);
    } catch (err) {
      // Roll back the optimistic user message so the user can retry
      // without a duplicate row in the conversation.
      setMessages((prev) => prev.slice(0, -1));
      setInput(draft);
      const msg =
        err instanceof Error ? err.message : "tutor request failed";
      // Friendlier copy on rate-limit responses — covers both our own
      // edge ratelimit and an upstream Anthropic 429. The raw "Too
      // Many Requests" string is technically correct but not what a
      // mid-quiz user wants to see.
      const isRateLimit = /\b429\b|rate.?limit|Too Many Requests/i.test(msg);
      // Raw detail goes to observa only — users never see the upstream
      // error string; they get friendly copy below.
      void observa.error({
        error: err instanceof Error ? err : new Error(msg),
        context: {
          sessionId: sessionIdRef.current,
          questionId: current.id,
          rateLimit: isRateLimit,
        },
      });
      setError(
        isRateLimit
          ? "You're going a bit fast — try again in a moment."
          : "The tutor is having trouble right now — try again in a moment.",
      );
    } finally {
      setSending(false);
    }
  }

  return {
    messages,
    serverSessionId,
    input,
    setInput,
    sending,
    error,
    userTurnCount,
    capped,
    send,
    seedKickoff,
    reset,
  };
}
